import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import AccountsPage from './pages/AccountsPage';
import PaymentsPage from './pages/PaymentsPage';
import FeesPage from './pages/FeesPage';
import FxPage from './pages/FxPage';
import CompliancePage from './pages/CompliancePage';
import AuditLogPage from './pages/AuditLogPage';
import ReportsPage from './pages/ReportsPage';
import SupportTicketsPage from './pages/SupportTicketsPage';
import MaintenanceFeePage from './pages/MaintenanceFeePage';
import SystemPage from './pages/SystemPage';
import TeamPage from './pages/TeamPage';
import LoginPage from './pages/LoginPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import MandatoryMfaSetupPage from './pages/MandatoryMfaSetupPage';
import SettingsPage from './pages/SettingsPage';
import ProvidersPage from './pages/ProvidersPage';
import ProviderDetailsPage from './pages/ProviderDetailsPage';
import CurrenciesPage from './pages/CurrenciesPage';
import CountriesPage from './pages/CountriesPage';
import SepaCountriesPage from './pages/SepaCountriesPage';
import ReconciliationPage from './pages/ReconciliationPage';
import SepaRecallsPage from './pages/SepaRecallsPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingDetailPage from './pages/OnboardingDetailPage';
import AdminBusinessOnboardingWizardPage from './pages/AdminBusinessOnboardingWizardPage';
import { useAdminAuthStore } from './store/adminAuthStore';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, adminUser, syncProfile } = useAdminAuthStore();
  const loc = useLocation();

  useEffect(() => {
    if (!isAuthenticated) return;
    void syncProfile();
  }, [isAuthenticated, syncProfile]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  /** Legacy sessions: API blocks non-whitelist routes until 2FA enabled. */
  if (adminUser && adminUser.twoFactorEnabled !== true && loc.pathname !== '/settings') {
    return <Navigate to="/settings" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/admin/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/setup-mfa" element={<MandatoryMfaSetupPage />} />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/team" element={<TeamPage />} />
          <Route path="/team" element={<Navigate to="/settings/team" replace />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/accounts" element={<AccountsPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/fees" element={<FeesPage />} />
          <Route path="/fx" element={<FxPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/support-tickets" element={<SupportTicketsPage />} />
          <Route path="/maintenance-fees" element={<MaintenanceFeePage />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="/providers" element={<ProvidersPage />} />
          <Route path="/providers/:id" element={<ProviderDetailsPage />} />
          <Route path="/currencies" element={<CurrenciesPage />} />
          <Route path="/countries" element={<CountriesPage />} />
          <Route path="/sepa-countries" element={<SepaCountriesPage />} />
          <Route path="/reconciliation" element={<ReconciliationPage />} />
          <Route path="/sepa-recalls" element={<SepaRecallsPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/onboarding/:applicationId/edit" element={<AdminBusinessOnboardingWizardPage />} />
          <Route path="/onboarding/:applicationId" element={<OnboardingDetailPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
